import type React from "react";
import { Grid, Paper, Typography, Box, CircularProgress } from "@mui/material";
import { GoogleMap, Marker, useJsApiLoader } from "@react-google-maps/api";
import { useMemo } from "react";

interface VenueMapProps {
  data: VenueMapData;
}

interface VenueMapVenue {
  id: number;
  name: string;
  address?: string | null;
  lat?: number | null;
  lng?: number | null;
  status?: "active" | "booked" | "maintenance";
}

interface VenueMapData {
  venues: VenueMapVenue[];
}

const containerStyle = {
  width: "100%",
  height: "100%",
};

const mapOptions = {
  disableDefaultUI: true,
  zoomControl: true,
  styles: [
    {
      featureType: "all",
      elementType: "geometry.fill",
      stylers: [{ weight: "2.00" }],
    },
    {
      featureType: "all",
      elementType: "geometry.stroke",
      stylers: [{ color: "#9c9c9c" }],
    },
    {
      featureType: "all",
      elementType: "labels.text",
      stylers: [{ visibility: "on" }],
    },
  ],
};

const VenueMap: React.FC<VenueMapProps> = ({ data }) => {
  const { isLoaded } = useJsApiLoader({
    id: "google-map-script",
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY || "",
    libraries: ["places"],
  });

  const hasCoords = (
    venue: VenueMapVenue
  ): venue is VenueMapVenue & { lat: number; lng: number } =>
    typeof venue.lat === "number" && typeof venue.lng === "number";

  const venuesWithCoords = data.venues.filter(hasCoords);

  const center = useMemo(() => {
    if (!venuesWithCoords.length) {
      return { lat: -17.824858, lng: 31.053028 };
    }

    const avgLat =
      venuesWithCoords.reduce((sum, v) => sum + (v.lat ?? 0), 0) /
      venuesWithCoords.length;
    const avgLng =
      venuesWithCoords.reduce((sum, v) => sum + (v.lng ?? 0), 0) /
      venuesWithCoords.length;

    return { lat: avgLat, lng: avgLng };
  }, [venuesWithCoords]);

  return (
    <Grid container spacing={2}>
      {/* Venue Tracker */}
      <Grid size={{ xs: 12, md: 12 }}>
        <Paper sx={{ p: 1, height: 280, boxShadow: 0 }}>
          <Typography>Venues view on map</Typography>
          <Box
            sx={{
              height: "85%",
              borderRadius: 1,
              position: "relative",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            {isLoaded ? (
              <GoogleMap
                mapContainerStyle={containerStyle}
                center={center}
                zoom={12}
                options={mapOptions}
                
              >
                {venuesWithCoords.map((venue) => (
                  <Marker
                    key={venue.id}
                    position={{ lat: venue.lat, lng: venue.lng }}
                    title={venue.name}
                    icon={{
                      path: window.google?.maps?.SymbolPath?.CIRCLE || 0,
                      fillColor:
                        venue.status === "active"
                          ? "#4caf50"
                          : venue.status === "booked"
                          ? "#2196f3"
                          : "#ff9800",
                      fillOpacity: 1,
                      strokeWeight: 0,
                      scale: 8,
                    }}
                  />
                ))}
              </GoogleMap>
            ) : (
              <Box
                display="flex"
                flexDirection="column"
                alignItems="center"
                justifyContent="center"
                height="100%"
              >
                <CircularProgress
                  size={40}
                  sx={{
                    color: "#614438",
                  }}
                />
                <Typography variant="body2" mt={2}>
                  Loading Map...
                </Typography>
              </Box>
            )}
          </Box>
        </Paper>
      </Grid>
    </Grid>
  );
};

export default VenueMap;
